import { Stack } from "@mui/material";
import { resources } from "acl/resources";
import React from "react";
import { exportComponentAsJPEG } from "react-component-export-image";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { ROUTE_PATHS } from "utils/routerDom";

import { IsAllowed } from "../../acl";
import { useCheckAllFilesStatus, useGetBuilding, useGetScenarios } from "../../api/hooks/allHooks";
import apiUrls from "../../api/urls";
import { ButtonNew, CustomizedDialogs, Icon } from "../../components";
import { eventNames, trackGAEvent, withGAEventTracking } from "../../functions/googleAnalytics";
import { theme } from "../../styles/theme";
import { customApiCall } from "../../utils/axios";
import { LoadingOrEmptyWrapper } from "../LoadingAndEmptyHandler";

export const BuildingPassportHeader = ({ componentRef, pageName, projectId }) => {
  const { t } = useTranslation();

  const { data: dataBuilding, isLoading } = useGetBuilding(projectId);
  const building = dataBuilding || {};

  const { user } = useSelector((store) => store.user);
  const successMessage = t("general.successfulFileUpload");
  const headerTitle = t("general.uploadFiles");
  const url = apiUrls.userFiles.upload;
  const queryParams = [
    { name: "project", value: building?.id },
    { name: "user", value: user?.user_id },
  ];

  const downloadAllFiles = () => {
    const isTechnical = pageName === "technicalData";
    const url = apiUrls.files.folders.download(projectId, isTechnical);
    const fileName = `${isTechnical ? "All technical files.zip" : "All passport files.zip"}`;
    const errorMessage = t("general.downloadFileProblem");
    customApiCall.download({ errorMessage, fileName, url });
  };
  const { data: allFilesStatus } = useCheckAllFilesStatus(projectId, pageName);
  const { data: scenarios } = useGetScenarios(projectId);

  return (
    <LoadingOrEmptyWrapper height="100%" showLoading={isLoading}>
      <Stack className="justify-between">
        {pageName !== "modernization" ? (
          <div>
            <h2 className=" text-lds-foreground-muted-subtle font-ds-label-md">
              {t("BuildingPassportSharedLayout.buildingPassport")}
            </h2>
            <h1 className="font-ds-display-lg text-lds-foreground-heading-initial">{building?.descriptive_name}</h1>
          </div>
        ) : (
          <div>
            <h2 className=" text-lds-foreground-muted-subtle font-ds-label-md">
              {t("BuildingPassportSharedLayout.energyConsultant")}
            </h2>
            <h1 className="font-ds-display-lg">{building?.descriptive_name}</h1>
          </div>
        )}
        {["buildingProfile", "modernization", "technicalData"].includes(pageName) && (
          <Stack spacing={theme.spacing(4)}>
            {pageName !== "modernization" && IsAllowed("TECHNICAL_DATA.DOWNLOAD_FILES") && (
              <ButtonNew
                disabled={allFilesStatus?.status !== 200}
                onClick={() => {
                  const eventName =
                    pageName === "buildingProfile" ? eventNames.BP_DOWNLOAD_ALL : eventNames.TD_DOWNLOAD_ALL;
                  withGAEventTracking(eventName, {}, downloadAllFiles);
                }}
                size="md"
                variant="secondary"
              >
                <Stack spacing={theme.spacing(1.5)}>
                  <Icon disabled={allFilesStatus?.status !== 200} iconName="Download" />
                  <p>{t("BuildingPassportSharedLayout.downloadAllFiles")}</p>
                </Stack>
              </ButtonNew>
            )}
            {pageName === "modernization" && IsAllowed("MODERNIZATION.DOWNLOAD_SCENARIOS") && (
              <ButtonNew
                disabled={!scenarios || scenarios?.length === 0}
                onClick={() =>
                  withGAEventTracking(eventNames.MODERN_DOWNLOAD_SCRNARIOS, {}, () =>
                    exportComponentAsJPEG(componentRef)
                  )
                }
                size="md"
                variant="secondary"
              >
                <Stack spacing={theme.spacing(1.5)}>
                  <Icon disabled={!scenarios || scenarios?.length === 0} iconName="Download" />
                  <p>{t("BuildingPassportSharedLayout.downloadScenarios")}</p>
                </Stack>
              </ButtonNew>
            )}
            {pageName === "technicalData" && IsAllowed("TECHNICAL_DATA.UPLOAD_FILES") && (
              <CustomizedDialogs
                Component={
                  <ButtonNew onClick={() => trackGAEvent(eventNames.TD_UPLOAD_FILES)} size="md" variant="primary">
                    <Stack spacing={theme.spacing(1.5)}>
                      <Icon color="var(--color-element-primary-on-primary)" iconName="Upload" />
                      <p> {t("BuildingPassportSharedLayout.uploadFiles")}</p>
                    </Stack>
                  </ButtonNew>
                }
                formDataFileKeyName="file"
                headerTitle={headerTitle}
                queryParams={queryParams}
                successMessage={successMessage}
                url={url}
              />
            )}
            {pageName === "buildingProfile" && (
              <>
                {IsAllowed(resources.ADD_BUILDING.VIEW) && (
                  <Link to={ROUTE_PATHS.editBuilding(projectId).navigateUrl}>
                    <ButtonNew size="md" variant="secondary">
                      {t("general.editInformation")}
                    </ButtonNew>
                  </Link>
                )}
                {IsAllowed("BUILDING_PROFILE.THREE_D_VIEWER") && (
                  <>
                    <a
                      href={apiUrls.redirects.threeDViewer(projectId, user?.access_token)}
                      onClick={() => trackGAEvent(eventNames.BP_DOWNLOAD_3DVIEWER)}
                      rel="noreferrer"
                      target="_blank"
                    >
                      <ButtonNew size="md" variant="primary">
                        <Stack spacing={theme.spacing(1.5)}>
                          <p> {t("BuildingPassportSharedLayout.threeDViewer")}</p>
                          <Icon color="var(--color-element-primary-on-primary)" iconName="ChevronRight" size="sm" />
                        </Stack>
                      </ButtonNew>
                    </a>
                  </>
                )}
              </>
            )}
          </Stack>
        )}
      </Stack>
    </LoadingOrEmptyWrapper>
  );
};
